import { NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { PAYMENT_SSE_EVENTS } from '../../shared/constants/realtime.constants';
import { RealtimeEventsService } from '../../services/realtime-events.service';

export interface PaymentRealtimeHandlers {
  onSuccess: (eventName: string, data: any) => void;
  onFailed: (eventName: string, data: any) => void;
  /** When true, skip named payment_* events in the catch-all (they have dedicated handlers). */
  skipNamedInCatchAll?: boolean;
}

/**
 * Bind payment success/fail events from the shared SSE stream.
 *
 * Contract:
 * - `GET /api/v1/events/stream` + Bearer JWT (fetch stream)
 * - Handshake `event: connected` — ignored here (RealtimeEventsService)
 * - Named events: payment_done | payment_failed | payment_succeeded | order_payment_done
 * - `realtime.on(type)` yields envelope `data`
 * - Envelope `channel` (order-{locationId}|order-{userId}) is metadata only — no subscribe
 * - Callers pass data into handlePaymentSuccess / handlePaymentFailed unchanged
 *
 * Does not disconnect the stream — callers only manage this Subscription.
 */
export function bindPaymentSseEvents(
  realtime: RealtimeEventsService,
  ngZone: NgZone,
  handlers: PaymentRealtimeHandlers
): Subscription {
  const sub = new Subscription();
  const named = new Set<string>(PAYMENT_SSE_EVENTS);

  // Ensure user-authenticated stream is up (shared with deploy pilot on main app).
  if (!realtime.isConnected() || realtime.getAuthMode() !== 'user') {
    realtime.connectUser();
  }

  for (const eventName of PAYMENT_SSE_EVENTS) {
    sub.add(
      realtime.on(eventName).subscribe((data) => {
        ngZone.run(() => {
          if (eventName === 'payment_failed') {
            handlers.onFailed(eventName, data);
          } else {
            handlers.onSuccess(eventName, data);
          }
        });
      })
    );
  }

  // Catch-all: unnamed events with status succeeded/failed (was Pusher bind_global).
  sub.add(
    realtime.events$.subscribe((evt) => {
      if (named.has(evt.type) || evt.type.startsWith('pusher:')) {
        return;
      }
      if (handlers.skipNamedInCatchAll && named.has(evt.type)) {
        return;
      }
      const data = evt.data as { status?: string } | null;
      if (!data || typeof data !== 'object') {
        return;
      }
      if (data.status === 'succeeded') {
        ngZone.run(() => handlers.onSuccess(evt.type, data));
      } else if (data.status === 'failed') {
        ngZone.run(() => handlers.onFailed(evt.type, data));
      }
    })
  );

  return sub;
}
